import * as React from "react";
import { Button } from "baseui/button";
import { useStyletron } from "baseui";
import { NewUserModal, EditUserModal } from "./userModals.js";
import axios from "axios";
import { ButtonGroup, SIZE } from "baseui/button-group";
import { ToasterContainer } from "baseui/toast";
import { Delete, Check } from "baseui/icon";
import "./tables.css";
import {
  StatefulDataTable,
  StringColumn,
  CustomColumn,
} from "baseui/data-table";

function Users() {
  const [css] = useStyletron();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Add missing states
  const [data, setData] = React.useState([]); // For storing user data
  const [isLoading, setIsLoading] = React.useState(true); // Loading state
  const [isUserModalOpen, setIsUserModalOpen] = React.useState(false);

  // const fetchData = async () => {
  //   try {
  //     const response = await axios.get(`${apiUrl}/api/users/list`); //
  //     const users = response.data.users;
  //     const locations = response.data.locations;
  //     const roles = response.data.roles;

  //     console.log("users", users);
  //     console.log("locations", locations);
  //     // Create a mapping of location IDs to location names
  //     const locationIdToNameMap = locations.reduce((acc, location) => {
  //       acc[location.id] = location.location_name;
  //       return acc;
  //     }, {});

  //     // Create a mapping of role IDs to role names
  //     const roleIdToNameMap = roles.reduce((acc, role) => {
  //       acc[role.id] = role.role_name;
  //       return acc;
  //     }, {});

  //     const updatedUsers = users.map((user) => ({
  //       ...user,
  //       location: locationIdToNameMap[user.location_id] || user.location,
  //       role: roleIdToNameMap[user.role_id] || user.role,
  //     }));

  //     setData(updatedUsers); // Set the user data
  //   } catch (error) {
  //     console.error("Error fetching data:", error);
  //   } finally {
  //     setIsLoading(false); // Set loading state to false
  //   }
  // };

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/users/list`);
      const users = response.data.users;
      const locations = response.data.locations.sort((a, b) => a.id - b.id);
      const roles = response.data.roles;

      // console.log("locations", locations);
      // Create a mapping of location IDs to location names
      const locationIdToNameMap = locations.reduce((acc, location) => {
        acc[location.id] = location.location_name;
        return acc;
      }, {});

      // Create a mapping of role IDs to role names
      const roleIdToNameMap = roles.reduce((acc, role) => {
        acc[role.id] = role.role_name;
        return acc;
      }, {});

      const updatedUsers = users.map((user) => ({
        ...user,
        // Assuming user.location_id is an array of location IDs
        location:
          user.location_id
            .slice() // Create a shallow copy to avoid mutating the original array
            .sort((a, b) => a - b) // Sort the location IDs in ascending order
            .map((id) => locationIdToNameMap[id]) // Map sorted IDs to location names
            .filter((name) => name) // Filter out any undefined names
            .join(", ") || "No Location",
        role: roleIdToNameMap[user.role_id] || user.role,
      }));

      setData(updatedUsers); // Set the user data
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false); // Set loading state to false
    }
  }, [apiUrl]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  // console.log("users", data);

  const handleUserModalClose = () => {
    setIsUserModalOpen(false);
    fetchData(); // Refresh user data when modal closes
  };

  /////////////////////////////////////////////////////////////
  //CREATING USER LIST (cols)
  const columns = [
    StringColumn({
      title: "username",
      mapDataToValue: (data) => data.username,
    }),
    StringColumn({
      title: "first name",
      mapDataToValue: (data) => data.first_name,
    }),
    StringColumn({
      title: "last name",
      mapDataToValue: (data) => data.last_name,
    }),
    StringColumn({
      title: "location",
      mapDataToValue: (data) => data.location,
    }),
    StringColumn({
      title: "role",
      mapDataToValue: (data) => data.role,
    }),
    StringColumn({
      title: "status",
      mapDataToValue: (data) => data.status,
    }),
    CustomColumn({
      title: "Actions",
      mapDataToValue: (data) => data,
      renderCell: function Cell(props) {
        // const [isOpen, setIsOpen] = React.useState(false);
        const [isEditUserModalOpen, setIsEditUserModalOpen] =
          React.useState(false);
        const handleEditUserModalClose = () => {
          setIsEditUserModalOpen(false);
          fetchData(); // Refresh user data when modal closes
        };

        const rowData = props.value;

        // Form values for EditUserModal
        const [formValue, setFormValue] = React.useState({
          id: rowData.id,
          username: rowData.username,
          password: "",
          first_name: rowData.first_name,
          last_name: rowData.last_name,
          location: rowData.location,
          status: rowData.status,
          role: rowData.role,
        });
        //deactivate FROM COLUMN BTN NOT EditUserModal
        const handleDeactivateClick = async () => {
          try {
            const response = await axios.post(`${apiUrl}/api/user/deactivate`, {
              id: rowData.id,
              // id: formValue.id,
            });
            if (response.status === 200) {
              window.location.reload();
            }
          } catch (error) {
            console.error("Error deactivating user:", error);
          }
        };
      

        return (
          <>
            <ButtonGroup size={SIZE.mini}>
              <Button
                onClick={() =>
                  /*  setIsOpen(true) */ setIsEditUserModalOpen(true)
                }
              >
                Edit User
              </Button>

              <Button onClick={handleDeactivateClick}>
                {formValue.status === "active" ? (
                  <>
                    <Delete className={css({ color: "red" })} /> Deactivate User
                  </>
                ) : (
                  <>
                    <Check className={css({ color: "green" })} /> Activate User
                  </>
                )}
              </Button>
            </ButtonGroup>
            <EditUserModal
              isOpen={isEditUserModalOpen}
              handleEditUserModalClose={handleEditUserModalClose}
              formValue={formValue}
              setFormValue={setFormValue}
              rowData={rowData}
            />
          </>
        );
      },
    }),
  ];

  const rows = data.map((item, index) => ({
    id: index,
    data: item,
  }));

  //ENTIRE COMPONENT RETURN
  return (
    <div>
      {/**************** USER LIST ****************/}

      <ToasterContainer />
      <div className="table-container">
        <StatefulDataTable
          className="table-container"
          columns={columns}
          rows={rows}
          isLoading={isLoading}
        />
      </div>

      <Button onClick={() => setIsUserModalOpen(true)}>Create New User</Button>

      <NewUserModal
        isOpen={isUserModalOpen}
        // setIsUserModalOpen={setIsUserModalOpen}
        handleUserModalClose={handleUserModalClose}
      />
    </div>
  );
}

export default Users;
